import { postJson } from '@/lib/apiConfig';

/** PETS-138: até 3 envios do e-mail de confirmação do orçamento. */
export const BUDGET_EMAIL_MAX_ATTEMPTS = 3;

const RETRY_DELAYS_MS = [800, 1600];

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error ?? '');
}

/**
 * A oportunidade já foi criada e a falha é só do e-mail de confirmação
 * (ex.: MailerSend instável devolvendo "Falha ao Enviar Orçamento").
 */
export function isOpportunityEmailFailure(error: unknown): boolean {
  const message = errorMessage(error)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  return (
    message.includes('falha ao enviar orcamento') ||
    message.includes('mailersend') ||
    message.includes('falha no envio do email') ||
    message.includes('falha ao enviar o email') ||
    message.includes('email de confirmacao')
  );
}

function httpStatus(error: unknown): number | null {
  const status = (error as { status?: number })?.status;
  return typeof status === 'number' && Number.isFinite(status) ? status : null;
}

function shouldRetryBudgetEmail(error: unknown): boolean {
  if (isOpportunityEmailFailure(error)) return true;
  const status = httpStatus(error);
  if (status == null) return true;
  return status === 408 || status === 429 || status >= 500;
}

function wait(ms: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

export class BudgetEmailDeliveryError extends Error {
  attempts: number;
  status: number | null;

  constructor(attempts: number, cause: unknown) {
    super(errorMessage(cause));
    this.name = 'BudgetEmailDeliveryError';
    this.attempts = attempts;
    this.status = httpStatus(cause);
  }
}

export async function sendBudgetConfirmationEmail(
  payload: {
    base64: string;
    email: string;
    /** PETS-151: primeiro nome usado em "Olá, {nome}!". */
    nome: string;
    /** PETS-151: id do pedido exibido no corpo do e-mail. */
    numero_pedido: string;
  },
  hooks?: {
    onAttempt?: (attempt: number, maxAttempts: number) => void;
    onAttemptFail?: (info: {
      attempt: number;
      maxAttempts: number;
      error: unknown;
      willRetry: boolean;
    }) => void;
  }
): Promise<{ attempts: number }> {
  let lastError: unknown;
  let failedAttempt = 1;

  for (let attempt = 1; attempt <= BUDGET_EMAIL_MAX_ATTEMPTS; attempt++) {
    hooks?.onAttempt?.(attempt, BUDGET_EMAIL_MAX_ATTEMPTS);
    try {
      await postJson('rEsr7KLt', 'email/pedido', payload);
      return { attempts: attempt };
    } catch (error) {
      lastError = error;
      failedAttempt = attempt;
      const willRetry =
        attempt < BUDGET_EMAIL_MAX_ATTEMPTS && shouldRetryBudgetEmail(error);
      hooks?.onAttemptFail?.({
        attempt,
        maxAttempts: BUDGET_EMAIL_MAX_ATTEMPTS,
        error,
        willRetry,
      });
      if (!willRetry) break;
      await wait(RETRY_DELAYS_MS[attempt - 1] ?? 1600);
    }
  }

  throw new BudgetEmailDeliveryError(failedAttempt, lastError);
}
